/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PersonalizationSwapTagsMode } from '@btag/bt-personalization';
import SizeTracker from '../TopNav/SizeTracker/SizeTracker';
import { Button, Dropdown, HorizontalRule, PanelItem, RadioList, Message, Toggle } from '../../componentLibrary';
import { MESSAGE_TYPE_MAP } from '../../componentLibrary/Message/Message';
import { DRAFT_ACCESS } from '../../constants/itemTypes/itemTypes';
import rules from '../../constants/businessRules/businessRules';
import * as Styled from './styles';

const getPersonalizationRecipients = (singleUseTags) => Object.keys(Object.values(singleUseTags)[0]?.previewData);

const PreviewLeftPanel = ({
    onCustomFontsRenderOptionsChange,
    isDraftOwner,
    hasCustomFonts,
    hasPersonalization,
    singleUseTags,
    onPersonalizationModeChange,
    onPersonalizationRecipientChange,
}) => {
    const [hideAllWarnings, setHideAllWarnings] = useState(false);
    const [showGmailTruncationWarning, setShowGmailTruncationWarning] = useState(false);
    const [renderWithEmbeddedCustomFonts, setRenderWithEmbeddedCustomFonts] = useState(false);
    const [personalizationMode, setPersonalizationMode] = useState(PersonalizationSwapTagsMode.NONE);

    const { ed484PersonalizationMsft, ed563MailMergeV2 } = useFlags();

    useEffect(() => {
        onCustomFontsRenderOptionsChange(renderWithEmbeddedCustomFonts);
    }, [renderWithEmbeddedCustomFonts, onCustomFontsRenderOptionsChange]);

    return (
        <Styled.LeftPanelDiv>
            <Styled.LeftPanelInnerDiv>
                <Styled.LeftPanelHeader>Preview Info</Styled.LeftPanelHeader>
                <HorizontalRule styling={{ marginBottom: 8 }} />
                <Styled.WarningToggleDiv>
                    <Styled.WarningToggleTextContainer>
                        <Styled.WarningToggleText>Hide all warnings</Styled.WarningToggleText>
                    </Styled.WarningToggleTextContainer>
                    {hideAllWarnings && showGmailTruncationWarning && (
                        <Button
                            label="There are warnings that are currently hidden and should be addressed"
                            type="simple"
                            width={24}
                            icon="exclamation-triangle"
                            color="#E82E41"
                            hasHoverEffects={false}
                            iconSize={16}
                            iconPadding={0}
                            marginBottom={0}
                            tooltipPadding="12px 6px"
                        />
                    )}
                    <Styled.ToggleDiv>
                        <Toggle value={hideAllWarnings} onToggle={() => setHideAllWarnings(!hideAllWarnings)} />
                    </Styled.ToggleDiv>
                </Styled.WarningToggleDiv>
                <HorizontalRule styling={{ marginBottom: 16 }} />
                {!hideAllWarnings && (
                    <>
                        {ed484PersonalizationMsft && hasPersonalization && (
                            <>
                                <Styled.LeftPanelHeader>Personalization</Styled.LeftPanelHeader>
                                <HorizontalRule styling={{ marginBottom: 8 }} />
                                <Dropdown
                                    options={
                                        ed563MailMergeV2 && isDraftOwner
                                            ? [
                                                  {
                                                      name: 'Default tags',
                                                      displayName: 'Default tags',
                                                      value: PersonalizationSwapTagsMode.NONE,
                                                  },
                                                  {
                                                      name: 'Fallback text',
                                                      displayName: 'Fallback text',
                                                      value: PersonalizationSwapTagsMode.FALLBACK,
                                                  },
                                                  {
                                                      name: 'Recipient data',
                                                      displayName: 'Recipient data',
                                                      value: PersonalizationSwapTagsMode.VALUE,
                                                  },
                                              ]
                                            : [
                                                  {
                                                      name: 'Default tags',
                                                      displayName: 'Default tags',
                                                      value: PersonalizationSwapTagsMode.NONE,
                                                  },
                                                  {
                                                      name: 'Fallback text',
                                                      displayName: 'Fallback text',
                                                      value: PersonalizationSwapTagsMode.FALLBACK,
                                                  },
                                              ]
                                    }
                                    onSelect={({ value }) => {
                                        setPersonalizationMode(value);
                                        onPersonalizationModeChange(value);
                                    }}
                                    width={200}
                                />
                                {ed563MailMergeV2 &&
                                    isDraftOwner &&
                                    personalizationMode === PersonalizationSwapTagsMode.VALUE &&
                                    !_isEmpty(singleUseTags) && (
                                        <Dropdown
                                            options={getPersonalizationRecipients(singleUseTags)?.map((contact) => ({
                                                name: contact,
                                                displayName: contact,
                                                value: contact,
                                            }))}
                                            onSelect={({ value }) => onPersonalizationRecipientChange(value)}
                                            width={200}
                                        />
                                    )}
                            </>
                        )}
                        {hasCustomFonts && (
                            <PanelItem
                                title="custom fonts"
                                tooltip="Allows you to use specific fonts your employees have installed on their devices"
                                maxHeight={200}
                                showWarning={renderWithEmbeddedCustomFonts}
                            >
                                <Styled.CustomFontsWarningContainer>
                                    <Message
                                        type={MESSAGE_TYPE_MAP.WARNING}
                                        padding="8px 4px 8px 8px"
                                        showIcon
                                        showDismiss={false}
                                    >
                                        <Styled.CustomFontsWarningTextContainer>
                                            <p>
                                                Your custom fonts will only work if all employees have the font files
                                                installed.
                                            </p>
                                            <Styled.CustomFontsLink
                                                rel="noopener noreferrer"
                                                target="_blank"
                                                href="https://support.staffbase.com/hc/en-us/articles/4412592744722"
                                            >
                                                Custom fonts help article
                                            </Styled.CustomFontsLink>
                                        </Styled.CustomFontsWarningTextContainer>
                                    </Message>
                                </Styled.CustomFontsWarningContainer>
                                <Styled.RadioListContainer>
                                    <RadioList
                                        options={['System custom fonts', 'Embedded custom fonts']}
                                        onSelect={(selectedIndex) =>
                                            setRenderWithEmbeddedCustomFonts(selectedIndex === 1)
                                        }
                                    />
                                </Styled.RadioListContainer>
                            </PanelItem>
                        )}
                        <PanelItem
                            title="gmail truncation"
                            tooltip="Will warn you when you are nearing or exceeding the size limit of 102KB for Gmail recipients"
                            maxHeight={50}
                            showWarning={showGmailTruncationWarning}
                        >
                            <Styled.SizeTrackerContainer>
                                <SizeTracker
                                    onSizeUpdate={(size) =>
                                        setShowGmailTruncationWarning(rules.GMAIL_MAX_CONTENT_SIZE - size < 0)
                                    }
                                />
                            </Styled.SizeTrackerContainer>
                        </PanelItem>
                    </>
                )}
            </Styled.LeftPanelInnerDiv>
        </Styled.LeftPanelDiv>
    );
};

PreviewLeftPanel.propTypes = {
    onCustomFontsRenderOptionsChange: PropTypes.func.isRequired,
    onPersonalizationModeChange: PropTypes.func.isRequired,
    onPersonalizationRecipientChange: PropTypes.func.isRequired,
    isDraftOwner: PropTypes.bool.isRequired,
    hasCustomFonts: PropTypes.bool.isRequired,
    hasPersonalization: PropTypes.bool.isRequired,
    singleUseTags: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => {
    const integration = _get(state, 'session.integration', 'LOCALHOST');
    const isIntegrationsEnabled = _get(state, 'session.user.isIntegrationsEnabled', true);
    const hasPersonalization =
        _get(state, 'session.user.features.EMAIL_DESIGNER_PERSONALIZATION', false) &&
        (integration === 'LOCALHOST' || integration === 'APP') &&
        !isIntegrationsEnabled;
    return {
        isDraftOwner: _get(state, 'session.collaboration.access', '') === DRAFT_ACCESS.OWNER,
        hasCustomFonts:
            _get(state, 'session.user.features.EMAIL_DESIGNER_CUSTOM_FONTS', false) &&
            !_isEmpty(_get(state, 'customFonts.data', {})),
        hasPersonalization,
        singleUseTags: _get(state, 'personalization.tag.tags.singleUse', {}),
    };
};

export default connect(mapStateToProps)(PreviewLeftPanel);
